import {
  GridColumnMenu,
  GridValidRowModel,
  useGridApiRef,
} from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { FieldValues } from "react-hook-form";
import { UseItecDataGridProps, UseItecDataGrid } from "./types";
import { FilterAlt } from "@mui/icons-material";
import { Box, IconButton } from "@mui/material";
import { useFilter, useCheckboxSelection } from "../common";
import { Filter } from "../Filter";

export function dataGridBase<
  TEntity extends GridValidRowModel,
  TFilter extends FieldValues = {}
>(
  props: UseItecDataGridProps<TEntity, TFilter>
): UseItecDataGrid<TEntity, TFilter> {
  const filter = useFilter<TFilter>({ filters: props.filters });

  const query = useQuery({
    queryKey: ["query", filter?.form.watch()],
    queryFn: () => props.getEntities(filter?.form.watch()),
  });

  const { data, refetch, isFetching, isError } = query;

  const apiRef = useGridApiRef();

  const { selectedRows } = useCheckboxSelection<TEntity>(
    data,
    props.getRowId || (() => 0),
    apiRef
  );
  // TODO: habría que mejorar la estructura del código para que cuando no se requiere
  // checkbox selection, no sea necesario el hook.

  return {
    datagridProps: {
      apiRef: apiRef,
      hideFooterPagination: true,
      columns: props.columns.map((col) => ({
        ...col,
        sortable: false,
        disableColumnMenu: !props.filters?.[col.field],
        renderHeader: (params) => {
          return (
            <Box sx={{ ":hover > *": { visibility: "visible" } }}>
              <strong style={{ marginRight: 10 }}>
                {params.colDef.headerName}
              </strong>
              <>
                {props.filters?.[col.field] && (
                  <IconButton
                    onClick={() => {
                      apiRef.current.showColumnMenu(params.colDef.field);
                      // apiRef.current.showFilterPanel(params.colDef.field);
                    }}
                    sx={{
                      visibility: !!filter?.form.getValues(
                        props.filters[col.field].name
                      )
                        ? "visible"
                        : "hidden",
                    }}
                  >
                    <FilterAlt fontSize="small" />
                  </IconButton>
                )}
              </>
            </Box>
          );
        },
      })),
      loading: isFetching,
      rows: data || [],
      slots: {
        columnMenu: (params) => {
          return (
            <GridColumnMenu
              {...params}
              slots={{
                columnMenuUserItem: (params) => {
                  if (!props.filters || !filter) return <></>;
                  const {
                    colDef: { field },
                  } = params;
                  // TODO: make field of type union of possible fields
                  const menuParams = props.filters[field];

                  if (!menuParams) return <></>;
                  
                  return (
                    <Filter
                      close={() => {
                        apiRef.current.hideColumnMenu();
                      }}
                      formMethods={filter.form}
                      menuParams={menuParams}
                    />
                  );
                },
                columnMenuSortItem: null,
                columnMenuFilterItem: null,
                columnMenuColumnsItem: null,
              }}
            />
          );
        },
      },
      checkboxSelection: props.checkboxSelection,
      rowSelectionModel: props.checkboxSelection
        ? selectedRows.map((row) => props.getRowId(row))
        : undefined,
      getRowId: props.getRowId,
    },
    control: {
      query: query,
      form: props.filters ? filter?.form : undefined,
      selectedRows,
    },
  };
}
